import { Calendar, Wallet } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { useBankAccounts, type BankAccount } from "@/hooks/use-bank-accounts";

export type CashFlowDetailsFiltersValue = {
  bankAccountId?: string; // undefined = all accounts
  dateFrom: string; // YYYY-MM-DD
  dateTo: string;   // YYYY-MM-DD
};

const ALL = "__all__";

function pad2(n: number) {
  return String(n).padStart(2, "0");
}
function toYMD(d: Date) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function addDays(d: Date, days: number) {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
}
function startOfPrevMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth() - 1, 1);
}
function endOfPrevMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 0);
}

export function CashFlowDetailsFilters(props: {
  value: CashFlowDetailsFiltersValue;
  onChange: (next: CashFlowDetailsFiltersValue) => void;
  className?: string;
  activeOnly?: boolean;
}) {
  const { value, onChange, className, activeOnly = true } = props;
  const { data: accounts, isLoading } = useBankAccounts(activeOnly);

  const setRange = (from: Date, to: Date) => {
    onChange({ ...value, dateFrom: toYMD(from), dateTo: toYMD(to) });
  };

  const setYesterday = () => {
    const t = new Date();
    const y = addDays(t, -1);
    setRange(y, y);
  };

  const setToday = () => {
    const t = new Date();
    setRange(t, t);
  };

  const setLast7Days = () => {
    const t = new Date();
    setRange(addDays(t, -6), t); // includes today
  };

  const setCurrentMonth = () => {
    const t = new Date();
    setRange(startOfMonth(t), t); // month-to-date
  };

  const setLastMonth = () => {
    const t = new Date();
    setRange(startOfPrevMonth(t), endOfPrevMonth(t));
  };

  const labelCls =
    "h-4 leading-4 text-[10px] font-black text-slate-500 uppercase tracking-widest";

  const quickBtnCls = cn(
    "h-7 px-3 rounded-lg border border-slate-200 bg-white",
    "text-xs font-semibold text-slate-700 hover:bg-slate-50",
    "whitespace-nowrap"
  );

  return (
    <div className={cn("w-full", className)}>
      {/* One responsive row that becomes a stacked grid */}
      <div
        className={cn(
          "grid grid-cols-1",
          // on large screens: account | dates | quick buttons
          "lg:grid-cols-[360px_1fr_auto] lg:items-end"
        )}
      >
        {/* Account */}
        <div className="grid gap-2">
          <div className={labelCls}>Account</div>

          {isLoading ? (
            <Skeleton className="h-10 w-full rounded-lg" />
          ) : (
            <Select
              value={value.bankAccountId ?? ALL}
              onValueChange={(v) =>
                onChange({ ...value, bankAccountId: v === ALL ? undefined : v })
              }
            >
              <SelectTrigger className="h-10 w-[300px] rounded-lg bg-slate-50 border-slate-200">
                <Wallet className="h-4 w-4 mr-2 text-slate-400" />
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value={ALL}>All accounts</SelectItem>

                {(accounts ?? []).map((a: BankAccount) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name}
                    {a.bank_name ? ` — ${a.bank_name}` : ""}
                    {a.currency ? ` (${a.currency})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Dates */}
        <div className="grid gap-2">
          <div className={labelCls}>Period</div>

          {/* Keep everything same height (h-10) */}
          <div className="h-10 flex items-center gap-2 px-2 bg-slate-50 border border-slate-200 rounded-lg shadow-sm">
            <Calendar className="h-4 w-4 text-slate-400" />

            <Input
              type="date"
              value={value.dateFrom}
              onChange={(e) => onChange({ ...value, dateFrom: e.target.value })}
              className={cn(
                "h-8 w-[140px] border-none bg-transparent hover:bg-white",
                "focus-visible:ring-0 text-xs font-semibold"
              )}
              title="Start Date"
            />

            <span className="text-slate-300">—</span>

            <Input
              type="date"
              value={value.dateTo}
              onChange={(e) => onChange({ ...value, dateTo: e.target.value })}
              className={cn(
                "h-8 w-[140px] border-none bg-transparent hover:bg-white",
                "focus-visible:ring-0 text-xs font-semibold"
              )}
              title="End Date"
            />
          </div>
        </div>

        {/* Quick ranges */}
        <div className="ml-4 lg:ml-3 flex lg:items-center">
          <div
            className={cn(
              "mt-2 lg:mt-0",
              "self-center my-auto"
            )}
          >
            <div className="flex flex-wrap gap-2 justify-center lg:justify-center">
              <button type="button" onClick={setYesterday} className={quickBtnCls}>
                Yesterday
              </button>
              <button type="button" onClick={setToday} className={quickBtnCls}>
                Today
              </button>
              <button type="button" onClick={setLast7Days} className={quickBtnCls}>
                Last 7 days
              </button>
              <button type="button" onClick={setCurrentMonth} className={quickBtnCls}>
                Current month
              </button>
              <button type="button" onClick={setLastMonth} className={quickBtnCls}>
                Last month
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Extra responsiveness: on very small screens make date inputs stack */}
      <div className="mt-0 lg:hidden" />
    </div>
  );
}